/**
 * Tab-scoped application session.
 *
 * Everything that describes "which organization, account and language this tab is working in" lives in sessionStorage,
 * which the browser keeps per tab: it survives a refresh and an authentication redirect in the same tab, a duplicated tab
 * starts with a copy, and after that the two tabs change independently. localStorage is shared by every tab of the origin,
 * so it only keeps the language preference that a brand-new tab starts from.
 */

export const TAB_KEYS = {
    partnerId: '_PartnerID',
    tenantId: '_TenantID',
    accountId: '_AccountID',
    lang: 'lang',
    multilingual: 'isMultilingualHeaderSupported',
    timeout: '_timeout',
    promptAccount: '_PromptAccount',
};

/** Shared keys older builds wrote to localStorage. They are removed, never imported into a tab. */
const LEGACY_SHARED_KEYS = [TAB_KEYS.partnerId, TAB_KEYS.tenantId, TAB_KEYS.multilingual, TAB_KEYS.timeout];

export const SUPPORTED_LANGS = ['en', 'jp'];
export const DEFAULT_LANG = 'en';

function tabStore(): Storage | null {
    try { return window.sessionStorage; } catch { return null; }
}

function sharedStore(): Storage | null {
    try { return window.localStorage; } catch { return null; }
}

function read(store: Storage | null, key: string): string | null {
    try { return store ? store.getItem(key) : null; } catch { return null; }
}

function write(store: Storage | null, key: string, value: string): void {
    try { store && store.setItem(key, value); } catch { /* storage full or blocked: the in-memory state still applies */ }
}

function remove(store: Storage | null, key: string): void {
    try { store && store.removeItem(key); } catch { /* ignore */ }
}

export function getTabValue(key: string): string | null {
    const value = read(tabStore(), key);
    // Older code stored null/undefined as text; treat those as missing.
    return value === null || value === '' || value === 'null' || value === 'undefined' ? null : value;
}

export function setTabValue(key: string, value: any): void {
    if (value === null || value === undefined || value === '') {
        remove(tabStore(), key);
        return;
    }
    write(tabStore(), key, String(value));
}

export function removeTabValue(key: string): void {
    remove(tabStore(), key);
}

/** Ends this tab's application session. Language is kept: it is a display preference, not part of the session. */
export function clearTabSession(): void {
    Object.values(TAB_KEYS)
        .filter((key) => key !== TAB_KEYS.lang)
        .forEach((key) => removeTabValue(key));
}

/** 'ja' (the ISO code) maps to the 'jp' translation file; anything unsupported is null. */
export function normalizeLang(lang: any): string | null {
    if (typeof lang !== 'string') return null;
    const value = lang.trim().toLowerCase();
    if (value === 'ja') return 'jp';
    return SUPPORTED_LANGS.includes(value) ? value : null;
}

/**
 * This tab's language: the tab's own choice, else the last language chosen in any tab (so a new tab opens in it),
 * else English. The result is pinned to the tab so later choices in other tabs do not change it.
 */
export function getTabLanguage(): string {
    const own = normalizeLang(read(tabStore(), TAB_KEYS.lang));
    if (own) return own;
    const lang = normalizeLang(read(sharedStore(), TAB_KEYS.lang)) || DEFAULT_LANG;
    write(tabStore(), TAB_KEYS.lang, lang);
    return lang;
}

export function setTabLanguage(lang: any): string {
    const value = normalizeLang(lang) || DEFAULT_LANG;
    write(tabStore(), TAB_KEYS.lang, value);
    write(sharedStore(), TAB_KEYS.lang, value);
    return value;
}

/** The multilingual flag belongs to the organization it was loaded for; another organization's value never applies. */
export function setTabMultilingual(partnerId: string, supported: boolean): void {
    if (!partnerId) return;
    setTabValue(TAB_KEYS.multilingual, JSON.stringify({ partnerId, supported: !!supported }));
}

export function isTabMultilingual(partnerId: string): boolean {
    if (!partnerId) return false;
    try {
        const value = JSON.parse(getTabValue(TAB_KEYS.multilingual) || 'null');
        return !!value && value.partnerId === partnerId && value.supported === true;
    } catch {
        return false;
    }
}

/**
 * Removes values older builds kept in shared localStorage: the selected organization and tenant, the multilingual flag,
 * and SDK token caches that now live per tab. A legacy 'ja' language preference is normalized; nothing is copied into the tab.
 */
export function cleanupLegacySharedStorage(sdkTokenKeys: string[] = []): void {
    const shared = sharedStore();
    LEGACY_SHARED_KEYS.concat(sdkTokenKeys).forEach((key) => remove(shared, key));
    const lang = read(shared, TAB_KEYS.lang);
    if (lang !== null) {
        const normalized = normalizeLang(lang);
        normalized ? write(shared, TAB_KEYS.lang, normalized) : remove(shared, TAB_KEYS.lang);
    }
}

/**
 * MSAL (cacheLocation 'localStorage' in older builds) indexes its entries under msal.token.keys.<clientId> and
 * msal.account.keys; this lists exactly those entries so they can be removed without touching anything else.
 */
export function legacyMsalCacheKeys(clientId: string): string[] {
    const shared = sharedStore();
    const keys: string[] = [];
    const parse = (key: string) => {
        try { return JSON.parse(read(shared, key) || 'null'); } catch { return null; }
    };
    if (clientId) {
        const tokenKeysKey = `msal.token.keys.${clientId}`;
        const tokenKeys = parse(tokenKeysKey);
        if (tokenKeys && typeof tokenKeys === 'object') {
            ['idToken', 'accessToken', 'refreshToken'].forEach((type) => {
                if (Array.isArray(tokenKeys[type])) keys.push(...tokenKeys[type].filter((k) => typeof k === 'string'));
            });
            keys.push(tokenKeysKey);
        }
        keys.push(`msal.${clientId}.active-account`, `msal.${clientId}.active-account-filters`);
    }
    const accountKeys = parse('msal.account.keys');
    if (Array.isArray(accountKeys)) {
        keys.push(...accountKeys.filter((k) => typeof k === 'string'));
        keys.push('msal.account.keys');
    }
    return keys;
}

/**
 * The organization context of this tab changes (another organization, sign-out, a different account). Requests started
 * before the change carry the old value and their responses are dropped, so they cannot fill the new organization's screen.
 */
let contextVersion = 0;

export function currentContextVersion(): number {
    return contextVersion;
}

export function advanceContextVersion(): number {
    return ++contextVersion;
}

/** The subject (sub/oid) of a JWT, without verifying it: only used to tell accounts apart, never to authorize. */
export function jwtSubject(token: string): string | null {
    try {
        const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const claims = JSON.parse(decodeURIComponent(escape(atob(payload))));
        return claims.sub || claims.oid || null;
    } catch {
        return null;
    }
}
